// i18n/index.ts — react-i18next bootstrap for Phase B.
//
// English bundles are statically imported so the first paint is never blank.
// Non-English bundles are lazy-loaded on demand via `loadLanguage(code)` when
// the user switches languages — see Phase A's first-visit detection + the
// account-language picker in AccountSettingsPage. The loader is best-effort:
// a missing bundle silently falls through to English via fallbackLng.
//
// Detection order:
//   1. localStorage["ember.lang"] — same key Phase A's anonymous detector
//      uses, so the picker + browser-detection agree across mounts.
//   2. navigator — only consulted when localStorage is empty.
// Phase A's `detectBrowserLanguage()` remains the canonical source for the
// signed-in flow (it pushes to /api/auth/language); this detector just
// provides a synchronous initial language at mount time.
//
// `_meta` is intentionally not present at the top level of each English
// bundle as a translatable string — the translator script preserves it
// verbatim and adds generatedAt / model / translatedBy for machine output.

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "./supportedLanguages";

// Static imports — every English namespace ships in the initial bundle so
// the app can render the moment React mounts.
import enCommon from "../locales/en/common.json";
import enAuth from "../locales/en/auth.json";
import enPaywall from "../locales/en/paywall.json";
import enSetup from "../locales/en/setup.json";
import enGame from "../locales/en/game.json";
import enBookshelf from "../locales/en/bookshelf.json";
import enReferrals from "../locales/en/referrals.json";
import enScenarios from "../locales/en/scenarios.json";
import enPublicReader from "../locales/en/public-reader.json";

/**
 * Namespaces shipped in this codebase. `public-reader` lands in Phase C
 * to host the on-the-fly translation UI strings (banner, progress,
 * badges). Like every other namespace, en is statically imported below;
 * non-English bundles lazy-load on `loadLanguage(code)`.
 */
export const NAMESPACES = [
  "common",
  "auth",
  "paywall",
  "setup",
  "game",
  "bookshelf",
  "referrals",
  "scenarios",
  "public-reader",
] as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      // Phase A persists the user's language to localStorage["ember.lang"];
      // honour it here so the picker + browser detection agree.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "ember.lang",
      caches: ["localStorage"],
    },
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        paywall: enPaywall,
        setup: enSetup,
        game: enGame,
        bookshelf: enBookshelf,
        referrals: enReferrals,
        scenarios: enScenarios,
        "public-reader": enPublicReader,
      },
    },
    returnNull: false,
  });

/**
 * Lazy-load and activate a non-English language bundle. Idempotent —
 * calling twice for the same code is a no-op. Silently warns when a
 * (lang, namespace) JSON file is missing; the fallback to English keeps
 * the UI usable.
 */
export async function loadLanguage(code: string): Promise<void> {
  if (!code || code === "en") {
    if (i18n.language !== "en") await i18n.changeLanguage("en");
    return;
  }
  if (i18n.hasResourceBundle(code, "common")) {
    // Bundle already loaded; just switch active language.
    if (i18n.language !== code) await i18n.changeLanguage(code);
    return;
  }
  for (const ns of NAMESPACES) {
    try {
      // Vite resolves the glob below at build time; the dynamic import
      // string is template-literal-driven so each (code, ns) chunk-splits.
      const mod = await import(`../locales/${code}/${ns}.json`);
      const payload = (mod as { default?: unknown }).default ?? mod;
      i18n.addResourceBundle(code, ns, payload, true, true);
    } catch (err) {
      // Missing bundle for this (lang, ns) — log + continue. The
      // fallbackLng="en" means lookups still resolve.
      console.warn(`[i18n] missing bundle: ${code}/${ns}`, err);
    }
  }
  await i18n.changeLanguage(code);
}

export default i18n;
